import React from 'react';
import { Activity, Package } from 'lucide-react';
import type { FlatBrandNode } from '../../../utils/brand';
import type { ProductDetailResponse } from '../../../utils/product';

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '활성',
  INACTIVE: '비활성',
  PENDING: '대기중',
  ARCHIVED: '보관됨',
};

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-emerald-50 text-emerald-800 ring-emerald-600/20';
    case 'INACTIVE':
      return 'bg-gray-100 text-gray-700 ring-gray-500/10';
    case 'PENDING':
      return 'bg-amber-50 text-amber-800 ring-amber-600/20';
    default:
      return 'bg-slate-100 text-slate-800 ring-slate-500/10';
  }
}

const SummaryItem: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex min-w-0 items-start justify-between gap-3 py-1.5">
    <div className="shrink-0 text-xs font-medium text-gray-500">{label}</div>
    <div className="min-w-0 text-sm font-medium text-gray-900 text-right break-words">{children}</div>
  </div>
);

export interface ProductOptionGroupConfigProductSummaryProps {
  product: ProductDetailResponse;
  brands: FlatBrandNode[];
}

const ProductOptionGroupConfigProductSummary: React.FC<ProductOptionGroupConfigProductSummaryProps> = ({
  product,
  brands,
}) => {
  const brandName = product.brandCode ? brands.find((b) => b.code === product.brandCode)?.name : undefined;
  const brandDisplay =
    !product.brandCode?.trim()
      ? '—'
      : brandName
        ? `${brandName} (${product.brandCode})`
        : product.brandCode;

  const statusLabel = STATUS_LABEL[product.status] ?? product.status;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm ring-1 ring-gray-900/5">
      <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900">
            <Package className="h-5 w-5 flex-shrink-0 text-gray-700" aria-hidden />
            <span className="min-w-0 truncate">
              {product.name || '—'}
              <span className="font-normal text-gray-500">({product.itemCode || '—'})</span>
            </span>
          </h3>
          <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${statusBadgeClass(product.status)}`}
            title="상품 상태"
          >
            <Activity className="h-3.5 w-3.5" aria-hidden />
            {statusLabel}
          </span>
        </div>
      </div>

      <div className="px-4 py-3 sm:px-5">
        <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          <div className="divide-y divide-gray-100">
            <SummaryItem label="브랜드">{brandDisplay}</SummaryItem>
            <SummaryItem label="가격">{Number(product.originalPrice ?? 0).toLocaleString()}원</SummaryItem>
            <SummaryItem label="판매가격">{Number(product.salePrice ?? 0).toLocaleString()}원</SummaryItem>
          </div>
          <div className="divide-y divide-gray-100 sm:border-l sm:border-gray-100 sm:pl-8">
            <SummaryItem label="옵션그룹 매핑 수">
              {product.mappingOptionGroupCount ?? 0}
            </SummaryItem>
            {product.comment ? (
              <SummaryItem label="비고">
                <span className="text-xs font-normal text-gray-600">{product.comment}</span>
              </SummaryItem>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductOptionGroupConfigProductSummary;
